import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const fmt = (v) => parseFloat(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function NewQuotePage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const queryClient = useQueryClient();

  // Form state
  const [originCity, setOriginCity] = useState('');
  const [originDistrict, setOriginDistrict] = useState('');
  const [destCity, setDestCity] = useState('');
  const [destDistrict, setDestDistrict] = useState('');
  const [distanceKm, setDistanceKm] = useState(0);
  const [tollFee, setTollFee] = useState(0);
  const [tollOverride, setTollOverride] = useState('');
  const [tollManual, setTollManual] = useState(false);

  const [pricePerKm, setPricePerKm] = useState(65);
  const [openingFee, setOpeningFee] = useState(5500);
  const [allowancePerKm, setAllowancePerKm] = useState(4);
  const [kdvRate, setKdvRate] = useState(0.10);

  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountRate, setDiscountRate] = useState(0);
  const [discountNote, setDiscountNote] = useState('');

  const [patientName, setPatientName] = useState('');
  const [patientSurname, setPatientSurname] = useState('');
  const [patientTc, setPatientTc] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const [patientDiagnosis, setPatientDiagnosis] = useState('');

  const [instId, setInstId] = useState('');
  const [instName, setInstName] = useState('');
  const [instContact, setInstContact] = useState('');
  const [instPhone, setInstPhone] = useState('');
  const [instAddress, setInstAddress] = useState('');

  const [notes, setNotes] = useState('');
  const [queried, setQueried] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Cities query
  const { data: citiesData } = useQuery({
    queryKey: ['tr-cities'],
    queryFn: async () => (await api.get('/quotes/cities')).data.data,
    staleTime: 1000 * 60 * 60,
  });
  const cities = citiesData || [];

  // Institutions query
  const { data: institutionsData } = useQuery({
    queryKey: ['quote-institutions'],
    queryFn: async () => (await api.get('/quotes/institutions')).data.data,
    staleTime: 1000 * 60 * 30,
  });
  const institutions = institutionsData || [];

  // Districts queries
  const { data: originDistrictsData } = useQuery({
    queryKey: ['tr-districts', originCity],
    queryFn: async () => (await api.get(`/quotes/districts/${encodeURIComponent(originCity)}`)).data.data,
    enabled: !!originCity,
    staleTime: 1000 * 60 * 60,
  });
  const originDistricts = originDistrictsData || [];

  const { data: destDistrictsData } = useQuery({
    queryKey: ['tr-districts', destCity],
    queryFn: async () => (await api.get(`/quotes/districts/${encodeURIComponent(destCity)}`)).data.data,
    enabled: !!destCity,
    staleTime: 1000 * 60 * 60,
  });
  const destDistricts = destDistrictsData || [];

  // Load existing quote for edit mode
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then((res) => {
      const q = res.data.data;
      setOriginCity(q.origin_city);
      setOriginDistrict(q.origin_district);
      setDestCity(q.destination_city);
      setDestDistrict(q.destination_district);
      setDistanceKm(q.distance_km);
      setTollFee(parseFloat(q.toll_fee));
      if (q.toll_fee_override != null) {
        setTollManual(true);
        setTollOverride(String(q.toll_fee_override));
      }
      setPricePerKm(parseFloat(q.price_per_km));
      setOpeningFee(parseFloat(q.ambulance_opening_fee));
      setAllowancePerKm(parseFloat(q.road_allowance_per_km));
      setKdvRate(parseFloat(q.kdv_rate));
      setDiscountEnabled(q.discount_enabled);
      setDiscountRate(parseFloat(q.discount_rate) || 0);
      setDiscountNote(q.discount_note || '');
      setPatientName(q.patient_name || '');
      setPatientSurname(q.patient_surname || '');
      setPatientTc(q.patient_tc || '');
      setPatientPhone(q.patient_phone || '');
      setPatientAge(q.patient_age ? String(q.patient_age) : '');
      setPatientDiagnosis(q.patient_diagnosis || '');
      setInstName(q.institution_name || '');
      setInstContact(q.institution_contact || '');
      setInstPhone(q.institution_phone || '');
      setInstAddress(q.institution_address || '');
      setInstId(q.institution_id || '');
      setNotes(q.notes || '');
      setQueried(true);
    });
  }, [editId]);

  // Calculation
  const calculation = useMemo(() => {
    if (!distanceKm || distanceKm <= 0) return null;
    const effectiveToll = tollManual && tollOverride !== '' ? parseFloat(tollOverride) || 0 : tollFee;
    const km_cost = distanceKm * pricePerKm;
    const road_allowance = distanceKm * allowancePerKm;
    const toll_cost = effectiveToll * 2;
    const subtotal = km_cost + openingFee + road_allowance + toll_cost;
    const kdv_amount = subtotal * kdvRate;
    const total_amount = subtotal + kdv_amount;
    let discount_amount = 0;
    let final_amount = total_amount;
    if (discountEnabled && discountRate > 0) {
      discount_amount = total_amount * (discountRate / 100);
      final_amount = total_amount - discount_amount;
    }
    return {
      km_cost, road_allowance, toll_cost, subtotal, kdv_amount,
      total_amount, discount_amount, final_amount, effectiveToll
    };
  }, [distanceKm, tollFee, tollManual, tollOverride, pricePerKm, openingFee, allowancePerKm, kdvRate, discountEnabled, discountRate]);

  // Distance lookup
  const handleLookup = async () => {
    if (!originCity || !originDistrict || !destCity || !destDistrict) return;
    try {
      const res = await api.get('/quotes/distance-lookup', {
        params: {
          origin_city: originCity,
          origin_district: originDistrict,
          destination_city: destCity,
          destination_district: destDistrict,
        },
      });
      setDistanceKm(res.data.data.distance_km);
      setTollFee(res.data.data.toll_fee);
      setQueried(true);
      if (!tollManual) setTollOverride('');
    } catch (err) {
      alert(err.response?.data?.error || 'Mesafe bulunamadı');
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!queried || !calculation) return;
    setSubmitting(true);
    try {
      const payload = {
        origin_city: originCity,
        origin_district: originDistrict,
        destination_city: destCity,
        destination_district: destDistrict,
        distance_km: distanceKm,
        toll_fee: tollFee,
        toll_fee_override: tollManual && tollOverride !== '' ? parseFloat(tollOverride) : null,
        price_per_km: pricePerKm,
        ambulance_opening_fee: openingFee,
        road_allowance_per_km: allowancePerKm,
        kdv_rate: kdvRate,
        discount_enabled: discountEnabled,
        discount_rate: discountRate,
        discount_note: discountNote || null,
        patient_name: patientName || null,
        patient_surname: patientSurname || null,
        patient_tc: patientTc || null,
        patient_phone: patientPhone || null,
        patient_age: patientAge ? parseInt(patientAge) : null,
        patient_diagnosis: patientDiagnosis || null,
        institution_name: instName || null,
        institution_contact: instContact || null,
        institution_phone: instPhone || null,
        institution_address: instAddress || null,
        notes: notes || null,
        status: 'draft',
      };

      let result;
      if (isEdit) {
        result = await api.put(`/quotes/${editId}`, payload);
      } else {
        result = await api.post('/quotes', payload);
      }

      queryClient.invalidateQueries(['quotes']);
      const newId = isEdit ? editId : result.data.data.id;
      navigate(`/quotes/${newId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Teklif oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const SectionTitle = ({ children }) => (
    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">{children}</h3>
  );

  const selectClass = "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medical-500 focus:border-transparent";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Teklif Düzenle' : 'Yeni Teklif Oluştur'}
        </h1>
      </div>

      {/* Section 1: Mesafe Sorgulama */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <SectionTitle>Güzergah Bilgileri</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nereden */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-red-600">Nereden</label>
            <div className="grid grid-cols-2 gap-3">
              <select value={originCity} onChange={(e) => { setOriginCity(e.target.value); setOriginDistrict(''); setQueried(false); }} className={selectClass}>
                <option value="">İl Seçin</option>
                {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <select value={originDistrict} onChange={(e) => { setOriginDistrict(e.target.value); setQueried(false); }} className={selectClass} disabled={!originCity}>
                <option value="">İlçe Seçin</option>
                {originDistricts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
          </div>
          {/* Nereye */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-green-600">Nereye</label>
            <div className="grid grid-cols-2 gap-3">
              <select value={destCity} onChange={(e) => { setDestCity(e.target.value); setDestDistrict(''); setQueried(false); }} className={selectClass}>
                <option value="">İl Seçin</option>
                {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <select value={destDistrict} onChange={(e) => { setDestDistrict(e.target.value); setQueried(false); }} className={selectClass} disabled={!destCity}>
                <option value="">İlçe Seçin</option>
                {destDistricts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            onClick={handleLookup}
            disabled={!originCity || !originDistrict || !destCity || !destDistrict}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8"
          >
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            Sorgula
          </Button>
        </div>

        {queried && distanceKm > 0 && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{originCity}/{originDistrict}</span>
              <ArrowRightIcon className="inline h-4 w-4 mx-2" />
              <span className="font-semibold">{destCity}/{destDistrict}</span>
              {' '}arasındaki mesafe{' '}
              <span className="text-xl font-bold text-medical-700">{distanceKm} KM</span>
            </p>
            <div className="mt-2 flex items-center justify-center gap-4">
              <span className="text-sm text-gray-500">Ücretli Yol: <span className="font-semibold">{fmt(tollFee)} TL</span></span>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tollManual} onChange={(e) => { setTollManual(e.target.checked); if (!e.target.checked) setTollOverride(''); }} className="rounded border-gray-300 text-medical-600 focus:ring-medical-500" />
                Manuel düzenle
              </label>
              {tollManual && (
                <Input
                  type="number"
                  value={tollOverride}
                  onChange={(e) => setTollOverride(e.target.value)}
                  placeholder="Yeni ücret"
                  className="w-28"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Fiyat Hesaplama */}
      {queried && calculation && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle>Fiyat Hesaplama</SectionTitle>
          <div className="space-y-2">
            <div className="flex justify-between text-sm py-2">
              <span className="text-gray-600">Mesafe Ücreti ({distanceKm} km × {fmt(pricePerKm)} TL)</span>
              <span className="font-medium">{fmt(calculation.km_cost)} TL</span>
            </div>
            <div className="flex justify-between text-sm py-2">
              <span className="text-gray-600">Ambulans Açılış Ücreti</span>
              <span className="font-medium">{fmt(openingFee)} TL</span>
            </div>
            <div className="flex justify-between text-sm py-2">
              <span className="text-gray-600">Yol Harcırahı ({distanceKm} km × {fmt(allowancePerKm)} TL)</span>
              <span className="font-medium">{fmt(calculation.road_allowance)} TL</span>
            </div>
            <div className="flex justify-between text-sm py-2">
              <span className="text-gray-600">Ücretli Yol - Gidiş Dönüş ({fmt(calculation.effectiveToll)} TL × 2)</span>
              <span className="font-medium">{fmt(calculation.toll_cost)} TL</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between text-sm font-semibold">
              <span>Ara Toplam</span>
              <span>{fmt(calculation.subtotal)} TL</span>
            </div>
            <div className="flex justify-between text-sm py-2">
              <span className="text-gray-600">KDV (%{Math.round(kdvRate * 100)})</span>
              <span className="font-medium">{fmt(calculation.kdv_amount)} TL</span>
            </div>
            <div className="border-t-2 border-gray-300 pt-2 flex justify-between text-base font-bold">
              <span>Genel Toplam</span>
              <span>{fmt(calculation.total_amount)} TL</span>
            </div>
            {discountEnabled && discountRate > 0 && (
              <>
                <div className="flex justify-between text-sm py-2 text-red-600">
                  <span>İskonto (%{discountRate})</span>
                  <span>-{fmt(calculation.discount_amount)} TL</span>
                </div>
                <div className="border-t-2 border-medical-600 pt-3 flex justify-between text-lg font-bold text-medical-700">
                  <span>Ödenecek Tutar</span>
                  <span>{fmt(calculation.final_amount)} TL</span>
                </div>
              </>
            )}
            {!(discountEnabled && discountRate > 0) && (
              <div className="border-t-2 border-medical-600 pt-3 flex justify-between text-lg font-bold text-medical-700">
                <span>Ödenecek Tutar</span>
                <span>{fmt(calculation.final_amount)} TL</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 3: Iskonto */}
      {queried && calculation && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <SectionTitle>İskonto</SectionTitle>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={discountEnabled}
                onChange={(e) => setDiscountEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-medical-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-medical-600"></div>
            </label>
          </div>
          {discountEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">İskonto Oranı (%)</label>
                <Input
                  type="number"
                  value={discountRate}
                  onChange={(e) => setDiscountRate(parseFloat(e.target.value) || 0)}
                  min={0} max={100} step={0.5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama <span className="text-xs text-gray-400">(Proformada görünmez)</span>
                </label>
                <Input
                  value={discountNote}
                  onChange={(e) => setDiscountNote(e.target.value)}
                  placeholder="İskonto sebebi..."
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section 4: Hasta Bilgileri */}
      {queried && calculation && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle>Hasta Bilgileri</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ad</label>
              <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Soyad</label>
              <Input value={patientSurname} onChange={(e) => setPatientSurname(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TC / Pasaport No</label>
              <Input value={patientTc} onChange={(e) => setPatientTc(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <Input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yaş</label>
              <Input type="number" value={patientAge} onChange={(e) => setPatientAge(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tanı / Açıklama</label>
              <Input value={patientDiagnosis} onChange={(e) => setPatientDiagnosis(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* Section 5: Kurum Bilgileri */}
      {queried && calculation && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle>Kurum Bilgileri</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Kurum Seçin</label>
              <select
                value={instId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setInstId(selectedId);
                  if (selectedId) {
                    const inst = institutions.find((i) => String(i.id) === selectedId);
                    if (inst) {
                      setInstName(inst.name || '');
                      setInstContact(inst.contact_person || '');
                      setInstPhone(inst.contact_phone || '');
                      setInstAddress(inst.address || '');
                    }
                  } else {
                    setInstName('');
                    setInstContact('');
                    setInstPhone('');
                    setInstAddress('');
                  }
                }}
                className={selectClass}
              >
                <option value="">Kurum seçin veya boş bırakın</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kurum Adı</label>
              <Input value={instName} onChange={(e) => setInstName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yetkili Kişi</label>
              <Input value={instContact} onChange={(e) => setInstContact(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <Input value={instPhone} onChange={(e) => setInstPhone(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
              <Input value={instAddress} onChange={(e) => setInstAddress(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {queried && calculation && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle>Ek Notlar</SectionTitle>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medical-500"
            placeholder="Teklif ile ilgili ek notlar..."
          />
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && calculation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Teklif Önizleme</h2>
              <button onClick={() => setShowPreview(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="text-center mb-6">
              <img src="https://emtambulans.com/wp-content/uploads/2025/04/Adsiz-tasarim-62-150x150.png" alt="EMT" className="h-16 mx-auto mb-2" />
              <h3 className="text-lg font-bold">PROFORMA FATURA</h3>
              <p className="text-sm text-gray-500">EMT AMBULANS</p>
            </div>

            <div className="space-y-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-4">
                <p><strong>Güzergah:</strong> {originCity}/{originDistrict} → {destCity}/{destDistrict}</p>
                <p><strong>Mesafe:</strong> {distanceKm} km</p>
              </div>

              {(patientName || patientSurname) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-semibold mb-1">Hasta Bilgileri</p>
                  <p>{patientName} {patientSurname} {patientTc && `(${patientTc})`}</p>
                </div>
              )}

              {instName && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-semibold mb-1">Kurum</p>
                  <p>{instName}</p>
                </div>
              )}

              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#1F4E79] text-white">
                    <th className="text-left p-2 rounded-tl-lg">Kalem</th>
                    <th className="text-right p-2">Detay</th>
                    <th className="text-right p-2 rounded-tr-lg">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="p-2">Mesafe Ücreti</td><td className="text-right p-2">{distanceKm} km × {fmt(pricePerKm)} TL</td><td className="text-right p-2 font-medium">{fmt(calculation.km_cost)} TL</td></tr>
                  <tr className="border-b bg-gray-50"><td className="p-2">Ambulans Açılış Ücreti</td><td className="text-right p-2"></td><td className="text-right p-2 font-medium">{fmt(openingFee)} TL</td></tr>
                  <tr className="border-b"><td className="p-2">Yol Harcırahı</td><td className="text-right p-2">{distanceKm} km × {fmt(allowancePerKm)} TL</td><td className="text-right p-2 font-medium">{fmt(calculation.road_allowance)} TL</td></tr>
                  <tr className="border-b bg-gray-50"><td className="p-2">Ücretli Yol (G/D)</td><td className="text-right p-2">{fmt(calculation.effectiveToll)} TL × 2</td><td className="text-right p-2 font-medium">{fmt(calculation.toll_cost)} TL</td></tr>
                  <tr className="border-t-2 font-semibold"><td className="p-2">Ara Toplam</td><td></td><td className="text-right p-2">{fmt(calculation.subtotal)} TL</td></tr>
                  <tr className="border-b"><td className="p-2">KDV (%{Math.round(kdvRate * 100)})</td><td></td><td className="text-right p-2">{fmt(calculation.kdv_amount)} TL</td></tr>
                  <tr className="border-t-2 font-bold text-base"><td className="p-2">Genel Toplam</td><td></td><td className="text-right p-2">{fmt(calculation.total_amount)} TL</td></tr>
                  {discountEnabled && discountRate > 0 && (
                    <>
                      <tr className="text-red-600"><td className="p-2">İskonto (%{discountRate})</td><td></td><td className="text-right p-2">-{fmt(calculation.discount_amount)} TL</td></tr>
                      <tr className="bg-[#1F4E79] text-white font-bold text-lg"><td className="p-2 rounded-bl-lg">Ödenecek Tutar</td><td></td><td className="text-right p-2 rounded-br-lg">{fmt(calculation.final_amount)} TL</td></tr>
                    </>
                  )}
                  {!(discountEnabled && discountRate > 0) && (
                    <tr className="bg-[#1F4E79] text-white font-bold text-lg"><td className="p-2 rounded-bl-lg">Ödenecek Tutar</td><td></td><td className="text-right p-2 rounded-br-lg">{fmt(calculation.final_amount)} TL</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowPreview(false)}>Kapat</Button>
              <Button onClick={() => { setShowPreview(false); handleSubmit(); }} disabled={submitting} className="bg-medical-600 hover:bg-medical-700 text-white">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {isEdit ? 'Güncelle' : 'Teklif Oluştur'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {queried && calculation && (
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/quotes')}>İptal</Button>
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <EyeIcon className="h-5 w-5 mr-2" />
            Önizleme
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-medical-600 hover:bg-medical-700 text-white">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {submitting ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Teklif Oluştur')}
          </Button>
        </div>
      )}
    </div>
  );
}
